import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CardData } from "@/components/layout/element-layout/grid-layout/GridLayout";
import { useToast } from "@/hooks/use-toast";
import { useConfirmation } from "@/hooks/use-confirmation";
import {
  createObject,
  fetchObjects,
  updateObject,
  sectionDataObject,
  ObjectItem,
  objectsStore,
} from "@/lib/stores/objectsStore";
import { ItemTypeConfig } from "@/pages/unified-items/configs/types";
import { addPendingChange } from "@/lib/stores/pendingChangesStore";

// Metadata fields that can be edited directly on the object
const METADATA_FIELD_MAP: Record<string, keyof ObjectItem> = {
  'Name': 'name',
  'Type': 'type',
  'Tags': 'tags',
  'Status': 'status',
  'Owner': 'owner',
  'Identifiers': 'identifiers',
  'Folder': 'folder',
};

// Fields that should be stored as arrays (comma-separated text input)
const ARRAY_FIELDS = new Set(['identifiers']);

export interface UseLibraryHandlersProps {
  data: any[];
  selectedGame: any;
  config: ItemTypeConfig;
  setIsEditModalOpen: (value: boolean) => void;
  setIsAddWizardOpen: (value: boolean) => void;
  setIsCreateWizardOpen: (value: boolean) => void;
  setSelectedGlossaryItem: (value: any) => void;
  setUpdateTrigger: React.Dispatch<React.SetStateAction<number>>;
}

export function useLibraryHandlers({
  data,
  selectedGame,
  config,
  setIsEditModalOpen,
  setIsAddWizardOpen,
  setIsCreateWizardOpen,
  setSelectedGlossaryItem,
  setUpdateTrigger,
}: UseLibraryHandlersProps) {
  const { toast } = useToast();
  const { confirmation } = useConfirmation();
  const navigate = useNavigate();

  const handleDelete = useCallback(
    async (id: string, gameId: string) => {
      const item = data.find((item: any) => item.id === id);
      const itemName = item?.name || "this item";

      const confirmDialog = confirmation({
        title: "Delete Library Object",
        description: `Are you sure you want to delete "${itemName}"? This action cannot be undone.`,
        confirmText: "Delete",
        cancelText: "Cancel",
        variant: "destructive",
        onConfirm: async () => {
          try {
            await config.handleDelete(id, gameId, toast);
            confirmDialog.dismiss();
          } catch (error) {
            console.error("Error deleting library object:", error);
            confirmDialog.dismiss();
            toast({
              title: "Error",
              description: "Failed to delete library object",
              variant: "destructive",
            });
          }
        },
        onCancel: () => {
          confirmDialog.dismiss();
        },
      });
    },
    [data, config, confirmation, toast]
  );

  const handleCardClick = useCallback(
    (card: CardData) => {
      navigate(`/library/${card.id}`, { state: { itemName: card.title } });
    },
    [navigate]
  );

  const handleAdd = useCallback(() => {
    // Navigate to new object page instead of opening wizard
    navigate('/library/new');
  }, [navigate]);

  const handleGlossaryItemCreated = useCallback(
    async (newItem: {
      id?: string;
      game_id?: string;
      name: string;
      tags: string[];
      data: sectionDataObject[];
      blueprint_id?: string | null;
      version?: {
        changes: Record<string, unknown>;
        version: number;
      };
      blueprint_version?: number;
      identifiers?: string[];
    }) => {
      try {
        if (!selectedGame?.id) {
          toast({
            title: "Error",
            description: "No game selected",
            variant: "destructive",
          });
          return;
        }

        const itemData = {
          game_id: newItem.game_id || selectedGame.id,
          name: newItem.name,
          type: "glossary",
          tags: newItem.tags || [],
          data: newItem.data,
          blueprint_id: newItem.blueprint_id || null,
          blueprint_version: newItem.blueprint_version || 1,
          identifiers: newItem.identifiers || [],
        };

        const createdItem = await createObject(itemData, toast);

        toast({
          title: "Success",
          description: `Glossary item "${createdItem.name}" created successfully`,
        });

        setIsAddWizardOpen(false);
        setIsCreateWizardOpen(false);
        setIsEditModalOpen(false);
        setSelectedGlossaryItem(null);

        try {
          await fetchObjects(selectedGame.id, toast?.id || undefined as string);
        } catch (error) {
          console.warn("Failed to refresh glossary items after creation:", error);
        }
      } catch (error) {
        console.error("Error creating glossary item:", error);
      }
    },
    [selectedGame, toast, setIsAddWizardOpen, setIsCreateWizardOpen, setIsEditModalOpen, setSelectedGlossaryItem]
  );

  const handleGlossaryItemUpdated = useCallback(
    async (updatedItem: {
      id?: string;
      game_id?: string;
      name: string;
      tags: string[];
      data: sectionDataObject[];
      blueprint_id?: string | null;
      version?: {
        changes: Record<string, unknown>;
        version: number;
      };
      blueprint_version?: number | { major: number; minor: number; patch: number };
      identifiers?: string[];
    }) => {
      try {
        if (!selectedGame?.id || !updatedItem.id) {
          toast({
            title: "Error",
            description: "No game selected or missing item ID",
            variant: "destructive",
          });
          return;
        }

        const itemData = {
          id: updatedItem.id,
          game_id: updatedItem.game_id || selectedGame.id,
          name: updatedItem.name,
          type: "glossary",
          tags: updatedItem.tags || [],
          data: updatedItem.data,
          blueprint_id: updatedItem.blueprint_id || null,
          blueprint_version: updatedItem.blueprint_version, // Pass through as-is (supports both number and object formats)
          identifiers: updatedItem.identifiers || [],
          status: updatedItem.status, // Include status field
          owner: updatedItem.owner, // Include owner field
          edit_permissions: updatedItem.edit_permissions, // Include edit_permissions field
          description: updatedItem.description, // Include description field
          folder: updatedItem.folder, // Include folder field
        };

        await updateObject(itemData, 'patch', undefined, toast);

        setUpdateTrigger((prev) => prev + 1);

        toast({
          title: "Success",
          description: `Glossary item "${updatedItem.name}" updated successfully`,
        });

        setIsEditModalOpen(false);
        setSelectedGlossaryItem(null);

        try {
          await fetchObjects(selectedGame.id, toast?.id || undefined as string);
        } catch (error) {
          console.warn("Failed to refresh glossary items after update:", error);
        }
      } catch (error) {
        console.error("Error updating glossary item:", error);
      }
    },
    [selectedGame, toast, setIsEditModalOpen, setSelectedGlossaryItem, setUpdateTrigger]
  );

  /**
   * Handle cell value changes from the table view
   * Saves changes locally only - use pending changes system to sync to backend
   * Supports both metadata fields (Name, Type, Tags, Status, Owner) and section fields
   */
  const handleCellValueChange = useCallback(
    (rowKey: string, cellKey: string, newValue: any) => {
      console.log('[handleCellValueChange] Called with:', { rowKey, cellKey, newValue });
      
      if (!selectedGame?.id) {
        toast({
          title: "Error",
          description: "No game selected",
          variant: "destructive",
        });
        return;
      }

      // Find the existing item
      const existingItem = data.find((item: ObjectItem) => item.id === rowKey) as ObjectItem | undefined;
      if (!existingItem) {
        toast({
          title: "Error",
          description: "Item not found",
          variant: "destructive",
        });
        return;
      }

      console.log('[handleCellValueChange] Existing item found:', existingItem.name);

      // Create a deep copy of the item to avoid mutation
      const updatedItemData: ObjectItem = JSON.parse(JSON.stringify(existingItem));

      // Check if this is a metadata field
      const metadataKey = METADATA_FIELD_MAP[cellKey];
      console.log('[handleCellValueChange] Metadata key lookup:', { cellKey, metadataKey });
      
      if (metadataKey) {
        // Update metadata field directly
        const oldValue = (updatedItemData as any)[metadataKey];
        
        // Handle array fields (like identifiers) - convert comma-separated string to array
        let processedValue = newValue;
        if (ARRAY_FIELDS.has(metadataKey)) {
          if (typeof newValue === 'string') {
            // Split by comma, trim whitespace, filter empty strings
            processedValue = newValue
              .split(',')
              .map((s: string) => s.trim())
              .filter((s: string) => s.length > 0);
          } else if (!Array.isArray(newValue)) {
            processedValue = [];
          }
        }
        
        (updatedItemData as any)[metadataKey] = processedValue;
        console.log('[handleCellValueChange] Updated metadata field:', { metadataKey, oldValue, newValue: processedValue });
      } else {
        // This is a section field - find and update it in the data array
        console.log('[handleCellValueChange] Looking for section field:', cellKey);
        console.log('[handleCellValueChange] Item data sections:', updatedItemData.data?.length || 0);
        
        let fieldFound = false;
        if (Array.isArray(updatedItemData.data)) {
          for (const section of updatedItemData.data) {
            console.log('[handleCellValueChange] Checking section:', section.title, 'fields:', section.fields?.length || 0);
            if (Array.isArray(section.fields)) {
              for (const field of section.fields) {
                console.log('[handleCellValueChange] Checking field:', { label: field.label, id: field.id, currentValue: field.value });
                // Match by field label (which is used as cellKey in the table)
                if (field.label === cellKey || field.id === cellKey) {
                  const oldValue = field.value;
                  field.value = newValue;
                  console.log('[handleCellValueChange] ✓ MATCHED! Updated section field:', { 
                    fieldLabel: field.label, 
                    fieldId: field.id, 
                    oldValue, 
                    newValue 
                  });
                  fieldFound = true;
                  break;
                }
              }
            }
            if (fieldFound) break;
          }
        } else {
          console.warn('[handleCellValueChange] updatedItemData.data is not an array:', updatedItemData.data);
        }

        if (!fieldFound) {
          console.warn(`[handleCellValueChange] ✗ Field "${cellKey}" not found in item data. Available fields:`, 
            updatedItemData.data?.flatMap(s => s.fields?.map(f => ({ label: f.label, id: f.id })) || [])
          );
          return;
        }
      }

      // Prepare the pending data payload
      const pendingData = {
        id: updatedItemData.id,
        game_id: updatedItemData.game_id,
        name: updatedItemData.name,
        type: updatedItemData.type || "glossary",
        tags: updatedItemData.tags || [],
        data: updatedItemData.data,
        blueprint_id: updatedItemData.blueprint_id || null,
        identifiers: updatedItemData.identifiers || [],
        status: updatedItemData.status,
        owner: updatedItemData.owner,
        folder: updatedItemData.folder,
      };

      console.log('='.repeat(60));
      console.log('[handleCellValueChange] SAVING LOCALLY');
      console.log('Cell edited:', cellKey);
      console.log('New value:', newValue);
      console.log('Full pending data object:', JSON.stringify(pendingData, null, 2));
      console.log('Data sections:', pendingData.data?.map((s: any) => ({
        title: s.title,
        fields: s.fields?.map((f: any) => ({ label: f.label, id: f.id, value: f.value }))
      })));
      console.log('='.repeat(60));

      // Track this change in the pending changes store
      addPendingChange(
        existingItem.id,
        'library',
        selectedGame.id,
        existingItem.version || null,
        existingItem, // Original data
        pendingData,  // Pending data with changes
        cellKey       // Which cell was modified
      );

      // Update the local store immediately for UI feedback (optimistic update)
      const currentData = objectsStore.getData();
      const updatedStoreData = currentData.map((item) =>
        item.id === rowKey ? { ...item, ...updatedItemData } : item
      );
      objectsStore.setData(updatedStoreData);

      setUpdateTrigger((prev) => prev + 1);

      console.log('[handleCellValueChange] Change saved locally. Use save button to sync to server.');
    },
    [data, selectedGame, toast, setUpdateTrigger]
  );

  return {
    handleDelete,
    handleCardClick,
    handleAdd,
    handleGlossaryItemCreated,
    handleGlossaryItemUpdated,
    handleCellValueChange,
  };
}

